import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Phone,
  Navigation,
  Globe,
  Star,
  Clock,
  Loader2,
  AlertCircle,
  Shield,
  Users,
  Edit3,
  Copy,
  ExternalLink,
  CheckCircle,
  Calendar,
  Hash,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import { toast } from "react-toastify";
import branchService from "../../../requests/branches/BranchService";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import { PERMISSIONS } from "../../../config/permissions";

const BranchDetails = () => {
  const { branchSlug } = useParams();
  const navigate = useNavigate();
  const [branchDetails, setBranchDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Permission checks
  const { canPerform, isAuthenticated, isMainVendor } = usePermissionCheck();

  // Check if user can view branch details
  const canViewBranchDetails =
    canPerform(
      PERMISSIONS.MODULES.BRANCH_MANAGEMENT,
      PERMISSIONS.ACTIONS.VIEW_BRANCHES
    ) || isMainVendor;

  const fetchBranchDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await branchService.getBranchDetails(branchSlug);

      if (response.status && response.data?.status_code === "000" && !response.data?.in_error) {
        // The API returns an array, so we take the first item
        const branchData = response.data?.data?.[0];
        if (branchData) {
          setBranchDetails(branchData);
        } else {
          setError("Branch details not found");
        }
      } else {
        setError(response.message || "Failed to fetch branch details");
      }
    } catch (error) {
      console.error("Error fetching branch details:", error);
      setError(error.message || "Failed to fetch branch details");
    } finally {
      setIsLoading(false);
    }
  }, [branchSlug]);

  useEffect(() => {
    if (canViewBranchDetails && branchSlug) {
      fetchBranchDetails();
    }
  }, [branchSlug, canViewBranchDetails, fetchBranchDetails]);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied to clipboard!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Access denied screen for users without view permissions
  if (!isAuthenticated || !canViewBranchDetails) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-[#FF6F6E]/5">
        <Navbar page="Branch Details" />
        <div className="flex justify-center items-center min-h-[80vh]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">
              Access Restricted
            </h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to view branch details. Please contact
              your administrator to request access to the Branch Management
              module.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Required Permission:
                </span>
              </div>
              <p className="text-sm text-blue-700">
                View Branches in Branch Management
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/branches")}
              className="px-6 py-3 mt-6 font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg transition-all duration-200 hover:from-blue-600 hover:to-blue-700"
            >
              Return to Branches
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-[#FF6F6E]/5">
        <Navbar page="Branch Details" />
        <div className="flex justify-center items-center min-h-[80vh]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col gap-4 items-center"
          >
            <Loader2 className="w-8 h-8 animate-spin text-[#FF6F6E]" />
            <p className="text-gray-600">Loading branch details...</p>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-[#FF6F6E]/5">
        <Navbar page="Branch Details" />
        <div className="flex justify-center items-center min-h-[80vh]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <AlertCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">
              Error Loading Branch Details
            </h2>
            <p className="mb-6 leading-relaxed text-gray-600">{error}</p>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/branches")}
                className="px-4 py-2 font-medium text-white bg-gray-500 rounded-lg transition-colors hover:bg-gray-600"
              >
                Back to Branches
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={fetchBranchDetails}
                className="px-4 py-2 bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] text-white rounded-lg hover:from-[#E55A59] hover:to-[#FF8584] transition-colors font-medium"
              >
                Try Again
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!branchDetails) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-[#FF6F6E]/5">
        <Navbar page="Branch Details" />
        <div className="flex justify-center items-center min-h-[80vh]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-gray-100 rounded-full">
              <Building2 className="w-12 h-12 text-gray-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">
              Branch Not Found
            </h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              The branch you're looking for doesn't exist or has been removed.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/branches")}
              className="px-6 py-3 font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg transition-all duration-200 hover:from-blue-600 hover:to-blue-700"
            >
              Return to Branches
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <Navbar page="Branch Details" />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen"
      >
        <div className="px-4 py-8 mx-auto max-w-7xl">
          {/* Enhanced Back Button */}
          <motion.button
            whileHover={{ scale: 1.02, x: -4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/branches")}
            className="flex gap-3 items-center px-4 py-3 mb-8 text-gray-600 rounded-xl backdrop-blur-sm transition-all duration-200 group hover:text-gray-900 hover:bg-white/60"
          >
            <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            <span className="font-medium">Back to Branches</span>
          </motion.button>

          {/* Enhanced Branch Header */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="overflow-hidden relative mb-8 bg-white rounded-3xl border shadow-2xl border-gray-200/60 shadow-blue-500/5"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-indigo-50/30 to-purple-50/50"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br rounded-full translate-x-32 -translate-y-32 from-blue-100/20 to-indigo-100/20"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr rounded-full -translate-x-24 translate-y-24 from-purple-100/20 to-pink-100/20"></div>
            
            <div className="relative p-8 lg:p-12">
              <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex gap-6 items-center">
                  <div className="relative">
                    <div className="p-6 rounded-3xl bg-gradient-to-br from-[#FF6F6E] via-[#FF8A8A] to-[#FF9A99] text-white shadow-2xl shadow-[#FF6F6E]/25">
                      <Building2 className="w-10 h-10" />
                    </div>
                    <div className="flex absolute -top-2 -right-2 justify-center items-center w-6 h-6 bg-green-500 rounded-full border-4 border-white">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <div>
                    <h1 className="mb-3 text-xl font-bold leading-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 sm:text-2xl md:text-3xl lg:text-4xl">
                      <span className="inline-block max-w-full break-words">{branchDetails.name}</span>
                    </h1>
                    <div className="flex flex-wrap gap-2 items-center sm:gap-3">
                      <div className="px-3 py-2 text-xs font-semibold text-green-800 whitespace-nowrap bg-gradient-to-r from-green-100 to-emerald-100 rounded-full border border-green-200 sm:px-4 sm:text-sm">
                        <div className="flex gap-1 items-center sm:gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span>Active Branch</span>
                        </div>
                      </div>
                      <div className="px-3 py-2 text-xs font-medium text-gray-700 whitespace-nowrap bg-gray-100 rounded-full sm:px-4 sm:text-sm">
                        <div className="flex gap-1 items-center sm:gap-2">
                          <Hash className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>ID: {branchDetails.branch_slug?.slice(0, 8)}...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate(`/edit-branch/${branchSlug}`)}
                    className="flex gap-2 justify-center items-center px-4 py-3 font-medium text-white whitespace-nowrap bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl sm:px-6"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span className="text-sm sm:text-base">Edit Branch</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (branchDetails.gpsAddress) {
                        window.open(`https://maps.google.com/?q=${encodeURIComponent(branchDetails.gpsAddress)}`, '_blank');
                      } else {
                        toast.info('GPS address not available for this branch');
                      }
                    }}
                    className="flex gap-2 justify-center items-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-200 hover:shadow-md sm:px-6"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm sm:text-base">View on Map</span>
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Branch Details Grid */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 gap-8 lg:grid-cols-2"
          >
            {/* Enhanced Location Information */}
            <div className="overflow-hidden relative bg-white rounded-3xl border shadow-xl transition-all duration-300 group border-gray-200/60 shadow-blue-500/5 hover:shadow-2xl hover:shadow-blue-500/10">
              <div className="absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 from-blue-50/30 to-indigo-50/20 group-hover:opacity-100"></div>
              <div className="relative p-8">
                <div className="flex gap-4 items-center mb-8">
                  <div className="p-4 text-white bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Location Details</h2>
                    <p className="text-gray-500">Complete address information</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-blue-200">
                    <div className="flex gap-3 items-center mb-3">
                      <Navigation className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Region</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900 break-words sm:text-xl">{branchDetails.region}</p>
                  </div>

                  <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-blue-200">
                    <div className="flex gap-3 items-center mb-3">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Location</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900 break-words sm:text-xl">{branchDetails.location}</p>
                  </div>

                  {branchDetails.district && (
                    <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-blue-200">
                      <div className="flex gap-3 items-center mb-3">
                        <Globe className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">District</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900 break-words sm:text-xl">{branchDetails.district}</p>
                    </div>
                  )}

                  {branchDetails.suburb && (
                    <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-blue-200">
                      <div className="flex gap-3 items-center mb-3">
                        <Star className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Suburb</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900 break-words sm:text-xl">{branchDetails.suburb}</p>
                    </div>
                  )}

                  {branchDetails.landmark && (
                    <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-blue-200">
                      <div className="flex gap-3 items-center mb-3">
                        <Star className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Landmark</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900 break-words sm:text-xl">{branchDetails.landmark}</p>
                    </div>
                  )}

                  {branchDetails.gpsAddress && (
                    <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-blue-200">
                      <div className="flex gap-3 items-center mb-3">
                        <Navigation className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">GPS Address</span>
                      </div>
                      <div className="flex gap-2 items-center">
                        <p className="flex-1 text-lg font-bold text-gray-900 break-words sm:text-xl">{branchDetails.gpsAddress}</p>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => copyToClipboard(branchDetails.gpsAddress, "GPS Address")}
                          className="p-2 text-gray-400 transition-colors hover:text-blue-600"
                        >
                          {copiedField === "GPS Address" ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <Copy className="w-5 h-5" />
                          )}
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Enhanced Contact Information */}
            <div className="overflow-hidden relative bg-white rounded-3xl border shadow-xl transition-all duration-300 group border-gray-200/60 shadow-purple-500/5 hover:shadow-2xl hover:shadow-purple-500/10">
              <div className="absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 from-purple-50/30 to-pink-50/20 group-hover:opacity-100"></div>
              <div className="relative p-8">
                <div className="flex gap-4 items-center mb-8">
                  <div className="p-4 text-white bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Contact & IDs</h2>
                    <p className="text-gray-500">Branch identification details</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {branchDetails.phone_number && (
                    <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-purple-200">
                      <div className="flex gap-3 items-center mb-3">
                        <Phone className="w-5 h-5 text-purple-600" />
                        <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Phone Number</span>
                      </div>
                      <div className="flex gap-2 items-center">
                        <p className="flex-1 text-lg font-bold text-gray-900 break-words sm:text-xl">{branchDetails.phone_number}</p>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => copyToClipboard(branchDetails.phone_number, "Phone Number")}
                          className="p-2 text-gray-400 transition-colors hover:text-purple-600"
                        >
                          {copiedField === "Phone Number" ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <Copy className="w-5 h-5" />
                          )}
                        </motion.button>
                      </div>
                    </div>
                  )}

                  <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-purple-200">
                    <div className="flex gap-3 items-center mb-3">
                      <Building2 className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Branch ID</span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <p className="flex-1 px-3 py-2 font-mono text-sm text-gray-700 break-all bg-gray-100 rounded-lg sm:text-lg">{branchDetails.branch_slug}</p>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => copyToClipboard(branchDetails.branch_slug, "Branch ID")}
                        className="p-2 text-gray-400 transition-colors hover:text-purple-600"
                      >
                        {copiedField === "Branch ID" ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </motion.button>
                    </div>
                  </div>

                  <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-purple-200">
                    <div className="flex gap-3 items-center mb-3">
                      <Users className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Vendor ID</span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <p className="flex-1 px-3 py-2 font-mono text-sm text-gray-700 break-all bg-gray-100 rounded-lg sm:text-lg">{branchDetails.vendor_slug}</p>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => copyToClipboard(branchDetails.vendor_slug, "Vendor ID")}
                        className="p-2 text-gray-400 transition-colors hover:text-purple-600"
                      >
                        {copiedField === "Vendor ID" ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Additional Information */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="overflow-hidden relative mt-8 bg-white rounded-3xl border shadow-xl transition-all duration-300 group border-gray-200/60 shadow-emerald-500/5 hover:shadow-2xl hover:shadow-emerald-500/10"
          >
            <div className="absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 from-emerald-50/30 to-teal-50/20 group-hover:opacity-100"></div>
            <div className="relative p-8">
              <div className="flex gap-4 items-center mb-8">
                <div className="p-4 text-white bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg">
                  <Calendar className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Branch Status & Timeline</h2>
                  <p className="text-gray-500">Current status and activity information</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-emerald-200">
                  <div className="flex gap-3 items-center mb-4">
                    <Clock className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Last Updated</span>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="p-3 bg-emerald-100 rounded-xl">
                      <Calendar className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">
                        {branchDetails.point_in_time ? 
                          new Date(branchDetails.point_in_time).toLocaleDateString() : 
                          'Not available'
                        }
                      </p>
                      <p className="text-sm text-gray-500">
                        {branchDetails.point_in_time ? 
                          new Date(branchDetails.point_in_time).toLocaleTimeString() : 
                          'No timestamp available'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-gradient-to-r from-gray-50 rounded-2xl border transition-all duration-200 group/item to-gray-100/50 border-gray-200/50 hover:border-emerald-200">
                  <div className="flex gap-3 items-center mb-4">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Branch Status</span>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="relative">
                      <div className="p-3 bg-green-100 rounded-xl">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">Active & Operational</p>
                      <p className="text-sm text-gray-500">Branch is currently active</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="pt-6 mt-8 border-t border-gray-200">
                <h3 className="mb-4 text-lg font-semibold text-gray-800">Quick Actions</h3>
                <div className="flex flex-wrap gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate(`/edit-branch/${branchSlug}`)}
                    className="flex gap-2 justify-center items-center px-3 py-2 text-xs font-medium text-white whitespace-nowrap bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl sm:px-4 sm:text-sm"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Details
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (branchDetails.google_url) {
                        window.open(`${branchDetails.google_url}`, '_blank');
                      } else {
                        toast.info('GPS address not available for this branch');
                      }
                    }}
                    className="flex gap-2 justify-center items-center px-3 py-2 text-xs font-medium text-gray-700 whitespace-nowrap bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-200 hover:shadow-md sm:px-4 sm:text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Location
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const allInfo = `Branch: ${branchDetails.name}\nRegion: ${branchDetails.region}\nLocation: ${branchDetails.location}\n${branchDetails.district ? `District: ${branchDetails.district}\n` : ''}${branchDetails.suburb ? `Suburb: ${branchDetails.suburb}\n` : ''}${branchDetails.landmark ? `Landmark: ${branchDetails.landmark}\n` : ''}${branchDetails.gpsAddress ? `GPS: ${branchDetails.gpsAddress}\n` : ''}${branchDetails.phone_number ? `Phone: ${branchDetails.phone_number}\n` : ''}Branch ID: ${branchDetails.branch_slug}\nVendor ID: ${branchDetails.vendor_slug}`;
                      copyToClipboard(allInfo, "All Branch Information");
                    }}
                    className="flex gap-2 justify-center items-center px-3 py-2 text-xs font-medium text-gray-700 whitespace-nowrap bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-200 hover:shadow-md sm:px-4 sm:text-sm"
                  >
                    <Copy className="w-4 h-4" />
                    Copy All Info
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default BranchDetails;
